"use client"

import * as React from "react"
import { Link, usePathname, useRouter } from "@/i18n/navigation"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import {getNavigation, MenuItem} from "@/lib/navigation-data"
import { LanguageSwitcher } from "@/components/language-switcher"
import DonistaWordmark from "@/components/icons/donista-wordmark"

export function ResponsiveNav({ rightSlot }: { rightSlot?: React.ReactNode } = {}) {
    const pathname = usePathname()
    const router = useRouter()
    const t = useTranslations("common")
    const tNav = useTranslations("navigation")
    const navigation = getNavigation(tNav)

    const [moreSheetOpen, setMoreSheetOpen] = React.useState(false)

    const isActive = (itemHref: string) => {
        if (itemHref === "/" && pathname === "/") {
            return true
        }
        return itemHref !== "/" && pathname.startsWith(itemHref)
    }

    const isParentActive = (item: MenuItem) => {
        if (isActive(item.href)) return true
        return item.children?.some((child) => isActive(child.href)) ?? false
    }

    const handleMobileNavClick = (item: MenuItem, e: React.MouseEvent) => {
        if (item.href === "#" && item.children) {
            e.preventDefault()
            setMoreSheetOpen(true)
        }
    }

    const handleNavigateAndClose = (href: string) => {
        setMoreSheetOpen(false)
        setTimeout(() => {
            router.push(href)
        }, 100)
    }

    const logoHref = pathname.startsWith("/start") ? "/home" : "/start"

    return (
        <>
            {/* Desktop Navigation - Top */}
            <div className="hidden md:block w-full border-b bg-background sticky top-0 z-50">
                <div className="w-full max-w-7xl mx-auto h-16 grid grid-cols-[auto_1fr_auto] items-center">
                    <Link href={logoHref} className="flex items-center shrink-0">
                        <DonistaWordmark priority />
                    </Link>
                    <NavigationMenu className="justify-self-center">
                            <NavigationMenuList className="gap-1">
                                {navigation.desktopNavigation.map((item) => (
                                    <NavigationMenuItem key={item.title}>
                                        {item.children ? (
                                            <>
                                                <NavigationMenuTrigger
                                                    className={cn(
                                                        "font-medium",
                                                        "bg-transparent hover:bg-transparent focus:bg-transparent",
                                                        "data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent",
                                                        "hover:text-primary focus:text-primary data-[state=open]:text-primary data-[state=open]:hover:text-primary data-[state=open]:focus:text-primary",
                                                        isParentActive(item) ? "text-primary" : "text-foreground",
                                                    )}
                                                >
                                                    {item.title}
                                                </NavigationMenuTrigger>
                                                <NavigationMenuContent>
                                                    <ul className="grid gap-1 p-3 w-[260px]">
                                                        {item.children.map((child) => (
                                                            <ListItem
                                                                key={child.title}
                                                                title={child.title}
                                                                href={child.href}
                                                                className={isActive(child.href) ? "text-primary" : ""}
                                                            >
                                                                {child.description}
                                                            </ListItem>
                                                        ))}
                                                    </ul>
                                                </NavigationMenuContent>
                                            </>
                                        ) : (
                                            <Link
                                                href={item.href}
                                                className={cn(
                                                    "inline-flex h-9 items-center justify-center rounded-md px-4 py-2",
                                                    "text-sm font-medium outline-none transition-[color,box-shadow]",
                                                    "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1",
                                                    "hover:text-primary focus:text-primary",
                                                    isActive(item.href) ? "text-primary" : "text-foreground",
                                                )}
                                            >
                                                {item.title}
                                            </Link>
                                        )}
                                    </NavigationMenuItem>
                                ))}
                            </NavigationMenuList>
                        </NavigationMenu>

                        {/* Desktop right slot: language toggle + install CTA + login button */}
                        <div className="flex items-center justify-end gap-2">
                            {rightSlot}
                        </div>
                    </div>
                </div>

            {/* Mobile Navigation - Bottom */}
            <div className={cn(
                "md:hidden fixed bottom-0 left-0 z-50 w-full h-16 border-t bg-background",
                moreSheetOpen && "hidden"
            )}>
                <div className="grid h-full grid-cols-4">
                    {navigation.mobileNavigation.map((item) => (
                        <React.Fragment key={item.title}>
                            {item.href === "#" && item.children ? (
                                <button
                                    onClick={(e) => handleMobileNavClick(item, e)}
                                    className={cn(
                                        "flex flex-col items-center justify-center h-full w-full gap-1",
                                        "text-muted-foreground hover:text-foreground transition-colors",
                                        "border-0 bg-transparent p-2",
                                    )}
                                >
                                    {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
                                    <span className="text-xs leading-tight">{item.title}</span>
                                </button>
                            ) : (
                                <Link
                                    href={item.href}
                                    className={cn(
                                        "flex flex-col items-center justify-center h-full w-full gap-1 p-2",
                                        isActive(item.href) ? "text-primary font-semibold" : "text-muted-foreground",
                                    )}
                                >
                                    {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
                                    <span className="text-xs leading-tight">{item.title}</span>
                                </Link>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* More Options Sheet for Mobile */}
            <Sheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen}>
                <SheetContent side="bottom" className="h-auto" aria-describedby={undefined}>
                    <div className="divide-y px-2 pb-2">
                        {navigation.desktopNavigation
                            .find((item) => item.key === "aboutUs")
                            ?.children?.map((child) => (
                                <button
                                    key={child.href}
                                    onClick={() => handleNavigateAndClose(child.href)}
                                    className={cn(
                                        "py-3 px-3 hover:bg-accent transition-colors w-full text-left text-sm font-medium",
                                        isActive(child.href) && "text-primary",
                                    )}
                                >
                                    {child.title}
                                </button>
                            ))}
                        <button
                            onClick={() => handleNavigateAndClose("/partner")}
                            className={cn(
                                "py-3 px-3 hover:bg-accent transition-colors w-full text-left text-sm font-medium",
                                isActive("/partner") && "text-primary",
                            )}
                        >
                            {t("nav.becomePartner")}
                        </button>
                        <button
                            onClick={() => handleNavigateAndClose("/about-us/mission")}
                            className={cn(
                                "py-3 px-3 hover:bg-accent transition-colors w-full text-left text-sm font-medium",
                                isActive("/about-us/mission") && "text-primary",
                            )}
                        >
                            {t("nav.aboutUs")}
                        </button>
                        <div className="py-3 px-3">
                            <LanguageSwitcher variant="toggle" />
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}

const ListItem = React.forwardRef<React.ElementRef<typeof Link>, React.ComponentPropsWithoutRef<typeof Link>>((
    { className, title, children, ...props }, ref) => {
    return (
        <li>
            <NavigationMenuLink asChild>
                <Link
                    ref={ref}
                    className={cn(
                        "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                        className,
                    )}
                    {...props}
                >
                    <div className="text-sm font-medium leading-none">{title}</div>
                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
                </Link>
            </NavigationMenuLink>
        </li>
    )
})
ListItem.displayName = "ListItem"
